import { Dispatch, SetStateAction } from 'react';
import { CurrentInstallation, MeasurementMap, UserInstallation } from '../../../../data-access/gql-types/graphql';
import { getCacheData } from '../../storage/cache';
import { useActionsStore } from '../../store/actions';
import { useCacheStore } from '../../store/cache';
import { useGroupsStore } from '../../store/groups';
import { useMeasurementMapStore } from '../../store/measurement-map';
import {
  UserActionsCollectionSegmentPick,
  UserGroupsCollectionSegmentPick,
  UserInstallationsCollectionSegmentPick,
} from '../../types';

type VerifyMethodParams = {
  updateInstallationList: (list: UserInstallation[]) => void;
  setListCacheEnabled: Dispatch<SetStateAction<boolean>>;
};

export const useVerifyCacheData = ({ updateInstallationList, setListCacheEnabled }: VerifyMethodParams) => {
  const setMeasurementMap = useMeasurementMapStore((s) => s.setMeasurementMap);
  const setActions = useActionsStore((s) => s.setActions);
  const setGroups = useGroupsStore((s) => s.setGroups);
  const { setMeasurementMapSkip, setActionsSkip, setGroupsSkip, setInstallationsSkip } = useCacheStore((s) => s);

  const verifyCacheData = (currentInstallation: CurrentInstallation) => {
    if (currentInstallation.cacheResponses) {
      const caches = currentInstallation.cacheResponses;
      const installationId = currentInstallation.installationId;

      // COMMON

      // Installations
      if (caches?.installationsCache) {
        const data = getCacheData<UserInstallationsCollectionSegmentPick>({
          kind: 'common',
          key: 'installations',
        });

        if (data?.cacheInfo?.hash === caches.installationsCache.hash) {
          updateInstallationList(data.items || []);
          setListCacheEnabled(true);
        } else setInstallationsSkip(false);
      } else setInstallationsSkip(false);

      // Measurements
      if (caches?.measurementMapCache) {
        const data = getCacheData<MeasurementMap>({ kind: 'common', key: 'measurementMap' });

        if (data?.cacheInfo?.hash === caches.measurementMapCache.hash) {
          setMeasurementMap(data);
        } else setMeasurementMapSkip(false);
      } else setMeasurementMapSkip(false);

      // PER INSTALLATION

      // Actions
      if (caches?.actionsCache) {
        const data = getCacheData<UserActionsCollectionSegmentPick>({
          kind: 'installation',
          key: 'actions',
          installationId,
        });

        if (data?.cacheInfo?.hash === caches.actionsCache.hash && data.cacheInfo.installationId === installationId) {
          setActions(data);
        } else setActionsSkip(false);
      } else setActionsSkip(false);

      // Groups
      if (caches?.groupsCache) {
        const data = getCacheData<UserGroupsCollectionSegmentPick>({
          kind: 'installation',
          key: 'groups',
          installationId,
        });

        if (data?.cacheInfo?.hash === caches.groupsCache.hash && data.cacheInfo.installationId === installationId) {
          setGroups(data);
        } else setGroupsSkip(false);
      } else setGroupsSkip(false);
    } else {
      setActionsSkip(false);
      setGroupsSkip(false);
      setInstallationsSkip(false);
      setMeasurementMapSkip(false);
    }
  };

  return {
    verifyCacheData,
  };
};
